import { LoadingButton } from '@mui/lab';
import { Box, Container, TextField, Typography } from '@mui/material';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Form } from 'react-router-dom';
import AppBar from '../app-bar/AppBar';
import avatar1 from '../assets/ksim/avatar-1.svg';
import avatar3 from '../assets/ksim/avatar-3.svg';
import tiles from '../assets/tiles-default.svg';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import EmailField from '../forms/email-field/EmailField';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  message: string;
}

const Contact = () => {
  const { control, formState, handleSubmit } = useForm<ContactFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      message: '',
    },
  });

  const onSubmit: SubmitHandler<ContactFormData> = (data, e) => {
    window.location.href = `mailto:support@ksimconnect.com?body=${encodeURIComponent(
      `Hello K-Sim Connect Support Team,\n\n${data.message}\n\ntel: ${data.phoneNumber}\ne-mail: ${data.email}\n\nBest regards,\n${data.firstName} ${data.lastName}`,
    )}`;

    e?.preventDefault();
  };

  return (
    <>
      <AppBar title="Contact support" />

      <Box
        sx={{
          background: { sm: `url(${tiles}) no-repeat right -280px bottom / 1276px 618px` },
          minHeight: 'calc(100vh -  48px)',
        }}
      >
        <Box
          alt="K-Sim"
          component="img"
          src={avatar1}
          sx={{
            display: { sm: 'none', md: 'inline' },
            position: 'absolute',
            width: 204,
            height: 294,
            right: 377,
            bottom: 328,
          }}
        />
        <Box
          alt="K-Sim"
          component="img"
          src={avatar3}
          sx={{
            display: { sm: 'none', md: 'inline' },
            position: 'absolute',
            width: 204,
            height: 294,
            right: 156,
            bottom: 329,
          }}
        />
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Help center', to: '/help-center' },
            { label: 'Contact support' },
          ]}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
          }}
        >
          <Box sx={{ maxWidth: '456px' }}>
            <Typography component="h1" variant="h3" pt={7} mb={3}>
              Get in touch with our team
            </Typography>
            <Typography component="p" variant="subtitle1" color="text.secondary" mb={6} fontWeight="bold">
              Please fill in the following details and a member of our team will be in touch shortly.
            </Typography>
            <Box
              component={Form}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              <Box flexDirection={{ xs: 'column', sm: 'row' }} sx={{ display: 'flex', gap: 2 }}>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: 'Please fill in your first name.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoComplete="off"
                      error={!!formState.errors?.firstName}
                      helperText={formState.errors?.firstName?.message as string}
                      label="First name"
                      placeholder="Enter your first name"
                      required
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={control}
                  rules={{ required: 'Please fill in your last name.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoComplete="off"
                      error={!!formState.errors?.lastName}
                      helperText={formState.errors?.lastName?.message as string}
                      label="Last name"
                      placeholder="Enter your last name"
                      required
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </Box>

              <EmailField control={control} size={'small'}></EmailField>

              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      inputMode: 'numeric',
                    }}
                    label="Phone number (optional)"
                    placeholder="Phone number"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Write your message here"
                    placeholder="Write your message here"
                    fullWidth
                    multiline
                    rows={4}
                  />
                )}
              />

              <LoadingButton sx={{ alignSelf: 'flex-start' }} type="submit" size="small" variant="contained">
                Submit
              </LoadingButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
